import { Form } from 'antd';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useAdminUsersQuery, useOrderByIdQuery, useUpdateOrderManagementMutation } from 'api';

interface UseAssignmentEditProps {
  onCancelModal: () => void;
  open: boolean;
}

export const useAssignmentEdit = (props: UseAssignmentEditProps) => {
  const { onCancelModal, open } = props;
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const { handleSuccess, handleError } = useNotificationMessages();
  const { data: userData = [] } = useAdminUsersQuery();
  const { data = {} } = useOrderByIdQuery({ id });

  const [updateOrderManagement, { isLoading: isLoadingUpdate }] = useUpdateOrderManagementMutation();
  const initialAssignment: Record<string, string> = useMemo(
    () => ({
      purchaseManagerId: data?.purchaseManager?.id,
      purchaseManagerAssistantId: data?.purchaseManagerAssistant?.id,
      salesManagerId: data?.salesManager?.id,
      salesManagerAssistantId: data?.salesManagerAssistant?.id,
      postSalesManagerId: data?.postSalesManager?.id,
      postSalesManagerAssistantId: data?.postSalesManagerAssistant?.id,
    }),
    [data]
  );
  const handleManagerChange = useCallback(
    (value: string, key: string) => {
      if (value === form.getFieldValue([key])) {
        form.setFieldsValue({ [key]: '' });
      }
    },
    [form]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    onCancelModal();
  }, [form, onCancelModal]);

  const onFinish = useCallback(async () => {
    try {
      const payload = form.getFieldsValue() || {};
      const isFormChanged = Object.keys(payload).some((key) => initialAssignment[key] !== payload[key]);

      if (!isFormChanged) {
        onCancel();
        return;
      }
      payload.id = id;
      const { data, error }: Record<string, any> = await updateOrderManagement(payload);
      if (data?.success) {
        handleSuccess('Order assignments were successfully updated.');
        onCancel();
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  }, [updateOrderManagement, handleSuccess, handleError, id, onCancel, form, initialAssignment]);

  useEffect(() => {
    if (open && initialAssignment) {
      form.setFieldsValue(initialAssignment);
    }
  }, [form, open, initialAssignment]);

  return {
    form,
    isLoadingUpdate,
    onFinish,
    onCancel,
    handleManagerChange,
    userData,
  };
};
